import React, { useState, useEffect } from "react";
import {
  Box,
  Grid,
  TextField,
  Typography,
  IconButton,
  Button,
  Snackbar,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Paper,
  Chip,
  Stack,
  alpha,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import {
  Close as CloseIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
} from "@mui/icons-material";
import {
  freightPayCreate,
  freightListforInvoiceCreation,
} from "../../../Services/Services";
import InfiniteScroll from "react-infinite-scroll-component";
import { brandColor as brand } from "../../../theme";
import { T } from "../../../theme";

// ─── Reuse same styles as parent ─────────────────────────────────
const sq = { borderRadius: "4px !important" };

const ghostBtn = {
  ...sq,
  border: `1.5px solid ${T.border}`,
  bgcolor: T.white,
  color: T.primary,
  fontWeight: 600,
  fontSize: "0.72rem",
  textTransform: "none",
  boxShadow: "none",
  "&:hover": {
    bgcolor: T.white,
    borderColor: T.borderHover,
    boxShadow: "none",
  },
};

const primaryBtn = {
  ...sq,
  bgcolor: T.primary,
  color: T.white,
  fontWeight: 700,
  fontSize: "0.72rem",
  textTransform: "none",
  boxShadow: "none",
  "&:hover": {
    bgcolor: T.primaryDark,
    boxShadow: "0 2px 8px rgba(79,70,229,0.3)",
  },
};

const dangerBtn = {
  ...sq,
  border: `1.5px solid ${T.redBorder}`,
  bgcolor: T.white,
  color: T.red,
  fontWeight: 600,
  fontSize: "0.72rem",
  textTransform: "none",
  boxShadow: "none",
  "&:hover": { bgcolor: T.redBg, borderColor: T.red, boxShadow: "none" },
};

const inputSx = {
  "& .MuiOutlinedInput-root": {
    ...sq,
    bgcolor: T.white,
    "& fieldset": { border: `1.5px solid ${T.border}` },
    "&:hover fieldset": { border: `1.5px solid ${T.borderHover}` },
    "&.Mui-focused fieldset": { border: `1.5px solid ${T.primary}` },
    "&.Mui-focused": { boxShadow: `0 0 0 3px ${T.primaryLight}` },
  },
  "& .MuiInputLabel-root.Mui-focused": { color: T.primary },
};

const disabledFieldSx = {
  width: "100%",
  "& .MuiOutlinedInput-root": {
    ...sq,
    bgcolor: "#f3f4f6",
    "& fieldset": { border: `1.5px solid ${T.border}` },
    "&.Mui-disabled fieldset": { border: `1.5px solid ${T.border}` },
  },
};

const tableInputSx = {
  "& .MuiOutlinedInput-root": {
    ...sq,
    height: 28,
    fontSize: "0.72rem",
    bgcolor: T.white,
    "& fieldset": { border: `1.5px solid ${T.border}` },
    "&:hover fieldset": { borderColor: T.borderHover },
    "&.Mui-focused fieldset": { border: `1.5px solid ${T.primary}` },
  },
  "& input": { py: 0.5, px: 1 },
};

// ─── Table styles matching parent component ───────────────────────
const tableSx = {
  borderCollapse: "separate",
  borderSpacing: 0,
  "& td, & th": {
    py: 0,
    px: 0.75,
    fontSize: "0.72rem",
    borderBottom: `1px solid #ccc`,
    whiteSpace: "nowrap",
    textAlign: "center",
    lineHeight: 1.2,
  },
  "& th": {
    fontWeight: 700,
    color: T.headerText,
    bgcolor: `${T.headerBg} !important`,
    py: 0.2,
    fontSize: "0.72rem",
    letterSpacing: "0.01em",
    borderBottom: `1px solid #ccc`,
    lineHeight: 1.2,
    height: 28,
  },
  "& td": { height: 36, py: 0.7 },
};

const EditFreightPayment = ({
  data = {},
  freightOptions = [],
  branchOptions = [],
  onClose,
  selectedFreight,
}) => {
  const [freightIds, setFreightIds] = useState([]);
  const [branchname, setBranchname] = useState("");
  const [totalAmount, setTotalAmount] = useState("");
  const [paidAmount, setPaidAmount] = useState("");
  const [remAmount, setRemAmount] = useState("");
  const [mergefreight, setFreightOptions] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMessage, setSnackMessage] = useState("");
  const [snackSeverity, setSnackSeverity] = useState("success");

  const [payments, setPayments] = useState([
    {
      pay_amount: "",
      payment_to: "",
      upi_check_no: "",
      pay_mode: "",
      remark: "",
    },
  ]);

  useEffect(() => {
    fetchFreightList(1);
  }, []);

  useEffect(() => {
    if (data) {
      setFreightIds(data.freight_id || []);
      setBranchname(data.branch_name || "");
      setTotalAmount(data.total_amount || "");
      setPaidAmount(data.paid_amount || "");
      setRemAmount(data.rem_amount || "");
      setPayments(data.payments?.length > 0 ? data.payments : []);
    }
  }, [data]);

  const fetchFreightList = async (pageNo = 1) => {
    setLoading(true);
    try {
      const response = await freightListforInvoiceCreation(pageNo);
      const newData = response.results || [];
      if (pageNo === 1) {
        setFreightOptions(selectedFreight?.concat(newData) || newData);
      } else {
        setFreightOptions((prev) => [...prev, ...newData]);
      }
      setHasMore(!!response.next);
      setPage((prev) => prev + 1);
    } catch (error) {
      console.error("Error fetching freight list", error);
      setSnackMessage("Failed to fetch freight list");
      setSnackSeverity("error");
      setSnackOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentChange = (index, field, value) => {
    const updated = [...payments];
    updated[index][field] = value;
    setPayments(updated);
    if (field === "pay_amount") {
      const newPaid = updated.reduce((acc, p) => {
        const val = parseFloat(p.pay_amount);
        return acc + (isNaN(val) ? 0 : val);
      }, 0);
      const total = parseFloat(totalAmount) || 0;
      setPaidAmount(newPaid.toFixed(2));
      setRemAmount((total - newPaid).toFixed(2));
    }
  };

  const handleAddPayment = () => {
    setPayments([
      ...payments,
      {
        pay_amount: "",
        payment_to: "",
        upi_check_no: "",
        pay_mode: "",
        remark: "",
      },
    ]);
  };

  const handleRemovePayment = (index) => {
    const updated = payments.filter((_, i) => i !== index);
    setPayments(updated);
    const newPaid = updated.reduce((acc, p) => {
      const val = parseFloat(p.pay_amount);
      return acc + (isNaN(val) ? 0 : val);
    }, 0);
    const total = parseFloat(totalAmount) || 0;
    setPaidAmount(newPaid.toFixed(2));
    setRemAmount((total - newPaid).toFixed(2));
  };

  const handleSubmit = async () => {
    if (payments.length === 0 || payments.every((p) => !p.pay_amount)) {
      setSnackMessage("Please add at least one payment");
      setSnackSeverity("error");
      setSnackOpen(true);
      return;
    }
    const formData = {
      freight_id: freightIds,
      branch_name: branchname,
      total_amount: parseFloat(totalAmount),
      paid_amount: parseFloat(paidAmount),
      rem_amount: parseFloat(remAmount),
      freight_data: mergefreight,
      payments: payments
        .filter((p) => p.pay_amount)
        .map((p) => ({
          pay_amount: parseFloat(p.pay_amount),
          payment_to: p.payment_to,
          upi_check_no: p.upi_check_no,
          pay_mode: p.pay_mode,
          remark: p.remark,
        })),
    };
    try {
      await freightPayCreate(data.freight_payment_id, formData);
      setSnackMessage("Payment updated successfully!");
      setSnackSeverity("success");
      setSnackOpen(true);
      setTimeout(() => {
        if (onClose) onClose();
      }, 1500);
    } catch (error) {
      setSnackMessage("Failed to update payment");
      setSnackSeverity("error");
      setSnackOpen(true);
    }
  };

  const handleIsBillToggle = (freightId) => {
    setFreightOptions((prevOptions) => {
      const updatedOptions = prevOptions.map((row) =>
        row.freight_id === freightId
          ? { ...row, is_invoice: !row.is_invoice }
          : row,
      );
      const newTotal = updatedOptions.reduce(
        (sum, row) =>
          row.is_invoice ? sum + (parseFloat(row.net_payable) || 0) : sum,
        0,
      );
      const newPaid = payments.reduce((acc, p) => {
        const val = parseFloat(p.pay_amount);
        return acc + (isNaN(val) ? 0 : val);
      }, 0);
      setFreightIds(
        updatedOptions.filter((r) => r.is_invoice).map((r) => r.freight_id),
      );
      setTotalAmount(newTotal.toFixed(2));
      setPaidAmount(newPaid.toFixed(2));
      setRemAmount((newTotal - newPaid).toFixed(2));
      return updatedOptions;
    });
  };

  const formatDate = (date) =>
    date ? new Date(date).toLocaleDateString("en-GB") : "—";

  const selectedCount = mergefreight.filter((r) => r.is_invoice).length;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <Snackbar
        open={snackOpen}
        autoHideDuration={3000}
        onClose={() => setSnackOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        sx={{ top: "80px !important" }}
      >
        <Alert
          onClose={() => setSnackOpen(false)}
          severity={snackSeverity}
          sx={{ ...sq }}
        >
          {snackMessage}
        </Alert>
      </Snackbar>

      {/* ─── Dialog Header ──────────────────────────────────── */}
      <Box
        sx={{
          background: "#6862e0",
          px: 2.5,
          py: 1.5,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography
            sx={{
              fontWeight: 700,
              color: T.white,
              fontSize: "0.88rem",
              letterSpacing: "0.01em",
            }}
          >
            Edit LR Payment
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            color: "rgba(255,255,255,0.8)",
            p: 0.5,
            ...sq,
            "&:hover": { bgcolor: "rgba(255,255,255,0.15)", color: T.white },
          }}
        >
          <CloseIcon sx={{ fontSize: 17 }} />
        </IconButton>
      </Box>

      {/* ─── Scrollable Content ──────────────────────────────── */}
      <Box
        sx={{
          flex: 1,
          overflow: "auto",
          px: 2,
          py: 1.5,
          bgcolor: T.white,
          "&::-webkit-scrollbar": { width: 5, height: 5 },
          "&::-webkit-scrollbar-thumb": {
            bgcolor: alpha(brand, 0.2),
            borderRadius: 3,
          },
        }}
      >
        {/* ─── Payment Summary ─────────────────────────────── */}
        <Box sx={{ mb: 1.5 }}>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: "0.72rem",
              color: T.primary,
              mb: 0.75,
            }}
          >
            Payment Summary
          </Typography>
          <Grid container spacing={1}>
            <Grid item xs={4}>
              <TextField
                label="Total Amount"
                type="number"
                fullWidth
                size="small"
                value={totalAmount}
                onChange={(e) => {
                  setTotalAmount(e.target.value);
                  const paid = parseFloat(paidAmount) || 0;
                  setRemAmount(
                    ((parseFloat(e.target.value) || 0) - paid).toFixed(2),
                  );
                }}
                sx={inputSx}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                label="Paid Amount"
                type="number"
                fullWidth
                size="small"
                value={paidAmount}
                InputProps={{ readOnly: true }}
                sx={disabledFieldSx}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                label="Remaining Amount"
                type="number"
                fullWidth
                size="small"
                value={remAmount}
                InputProps={{ readOnly: true }}
                sx={disabledFieldSx}
              />
            </Grid>
          </Grid>
        </Box>

        {/* ─── Payment Entries ─────────────────────────────── */}
        <Box sx={{ mb: 1.5 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 0.75,
            }}
          >
            <Typography
              sx={{ fontWeight: 700, fontSize: "0.72rem", color: T.primary }}
            >
              Payment Entries
            </Typography>
            <Chip
              label={`${payments.length} entries`}
              size="small"
              sx={{
                ...sq,
                height: 16,
                bgcolor: T.primaryBg,
                color: T.primary,
                border: `1px solid ${T.border}`,
                fontSize: "0.58rem",
                fontWeight: 600,
                "& .MuiChip-label": { px: 0.75 },
              }}
            />
          </Box>

          <Paper
            elevation={0}
            sx={{
              ...sq,
              border: `1.5px solid ${T.border}`,
              overflow: "hidden",
            }}
          >
            {/* Payment Table Header */}
            <Table size="small" sx={tableSx}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 30 }}>#</TableCell>
                  <TableCell>Pay Amount</TableCell>
                  <TableCell>Payment To</TableCell>
                  <TableCell>UPI/Cheque No.</TableCell>
                  <TableCell>Pay Mode</TableCell>
                  <TableCell>Remark</TableCell>
                  <TableCell sx={{ width: 40 }}></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {payments.map((payment, index) => {
                  const isEven = index % 2 === 0;
                  const rowBg = isEven ? T.baseRow : T.altRow;
                  return (
                    <TableRow
                      key={index}
                      sx={{
                        "& > td": { bgcolor: rowBg },
                        "&:hover > td": { bgcolor: `${T.rowHover} !important` },
                      }}
                    >
                      <TableCell>
                        <Typography
                          sx={{
                            fontSize: "0.65rem",
                            fontWeight: 700,
                            color: T.textMuted,
                          }}
                        >
                          {index + 1}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          type="number"
                          placeholder="₹"
                          value={payment.pay_amount}
                          onChange={(e) =>
                            handlePaymentChange(
                              index,
                              "pay_amount",
                              e.target.value,
                            )
                          }
                          sx={{ width: 100, ...tableInputSx }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          placeholder="Party/Vendor"
                          value={payment.payment_to}
                          onChange={(e) =>
                            handlePaymentChange(
                              index,
                              "payment_to",
                              e.target.value,
                            )
                          }
                          sx={{ width: 190, ...tableInputSx }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          placeholder="Reference"
                          value={payment.upi_check_no}
                          onChange={(e) =>
                            handlePaymentChange(
                              index,
                              "upi_check_no",
                              e.target.value,
                            )
                          }
                          sx={{ width: 150, ...tableInputSx }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          placeholder="Cash/UPI"
                          value={payment.pay_mode}
                          onChange={(e) =>
                            handlePaymentChange(
                              index,
                              "pay_mode",
                              e.target.value,
                            )
                          }
                          sx={{ width: 100, ...tableInputSx }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          placeholder="Note"
                          value={payment.remark}
                          onChange={(e) =>
                            handlePaymentChange(index, "remark", e.target.value)
                          }
                          sx={{ width: 150, ...tableInputSx }}
                        />
                      </TableCell>
                      <TableCell>
                        <Tooltip title="Remove" arrow>
                          <IconButton
                            size="small"
                            onClick={() => handleRemovePayment(index)}
                            sx={{
                              width: 20,
                              height: 20,
                              ...sq,
                              color: T.red,
                              bgcolor: T.white,
                              border: `1px solid ${T.redBorder}`,
                              "&:hover": { bgcolor: T.redBg },
                            }}
                          >
                            <DeleteIcon sx={{ fontSize: 11 }} />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>

            {/* Add Payment Row Button */}
            <Box
              sx={{
                px: 1.5,
                py: 0.75,
                borderTop: `1px solid ${T.border}`,
                bgcolor: T.white,
              }}
            >
              <Button
                startIcon={<AddIcon sx={{ fontSize: 13 }} />}
                size="small"
                onClick={handleAddPayment}
                sx={{ ...ghostBtn, height: 28, px: 1.5, fontSize: "0.68rem" }}
              >
                Add Payment Row
              </Button>
            </Box>
          </Paper>
        </Box>

        {/* ─── LR Records Table ───────────────────────── */}
        <Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 0.75,
            }}
          >
            <Typography
              sx={{ fontWeight: 700, fontSize: "0.72rem", color: T.primary }}
            >
              Select LR Records
            </Typography>
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Chip
                label={`${selectedCount} selected`}
                size="small"
                sx={{
                  ...sq,
                  height: 16,
                  bgcolor: selectedCount > 0 ? T.successBg : T.primaryBg,
                  color: selectedCount > 0 ? T.success : T.primary,
                  border: `1px solid ${selectedCount > 0 ? T.successBorder : T.border}`,
                  fontSize: "0.58rem",
                  fontWeight: 600,
                  "& .MuiChip-label": { px: 0.75 },
                }}
              />
              <Chip
                label={`${mergefreight.length} total`}
                size="small"
                sx={{
                  ...sq,
                  height: 16,
                  bgcolor: T.primaryBg,
                  color: T.primary,
                  border: `1px solid ${T.border}`,
                  fontSize: "0.58rem",
                  fontWeight: 600,
                  "& .MuiChip-label": { px: 0.75 },
                }}
              />
            </Stack>
          </Box>

          <Paper
            elevation={0}
            sx={{
              ...sq,
              border: `1.5px solid ${T.border}`,
              overflow: "hidden",
              boxShadow: "0 1px 8px rgba(79,70,229,0.06)",
            }}
          >
            <Box
              id="freightScrollDiv"
              sx={{
                maxHeight: 280,
                overflow: "auto",
                "&::-webkit-scrollbar": { width: 5, height: 5 },
                "&::-webkit-scrollbar-thumb": {
                  bgcolor: alpha(brand, 0.2),
                  borderRadius: 3,
                },
              }}
            >
              <InfiniteScroll
                dataLength={mergefreight.length}
                next={() => fetchFreightList(page)}
                hasMore={hasMore}
                loader={
                  <Box sx={{ textAlign: "center", py: 1 }}>
                    <CircularProgress size={16} sx={{ color: brand }} />
                  </Box>
                }
                scrollableTarget="freightScrollDiv"
              >
                <TableContainer>
                  <Table stickyHeader size="small" sx={tableSx}>
                    <TableHead>
                      <TableRow>
                        <TableCell
                          sx={{
                            position: "sticky",
                            left: 0,
                            zIndex: 4,
                            bgcolor: `${T.headerBg} !important`,
                            width: 36,
                          }}
                        />
                        <TableCell
                          sx={{
                            position: "sticky",
                            left: 36,
                            zIndex: 4,
                            bgcolor: `${T.headerBg} !important`,
                            width: 32,
                          }}
                        >
                          #
                        </TableCell>
                        <TableCell>LR No</TableCell>
                        <TableCell>Party Name</TableCell>
                        <TableCell>Truck No</TableCell>
                        <TableCell>From</TableCell>
                        <TableCell>To</TableCell>
                        <TableCell>Load Wt</TableCell>
                        <TableCell>Unload Wt</TableCell>
                        <TableCell>Net Payable</TableCell>
                        <TableCell>Date</TableCell>
                        <TableCell>Truck Owner</TableCell>
                        <TableCell>Remark</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {loading && mergefreight.length === 0 ? (
                        <TableRow>
                          <TableCell
                            colSpan={13}
                            align="center"
                            sx={{ py: 4, border: "none" }}
                          >
                            <CircularProgress size={24} sx={{ color: brand }} />
                          </TableCell>
                        </TableRow>
                      ) : mergefreight.length === 0 ? (
                        <TableRow>
                          <TableCell
                            colSpan={13}
                            align="center"
                            sx={{
                              py: 4,
                              border: "none",
                              color: T.textSub,
                              fontSize: "0.75rem",
                            }}
                          >
                            No freight records found
                          </TableCell>
                        </TableRow>
                      ) : (
                        mergefreight.map((row, index) => {
                          const isEven = index % 2 === 0;
                          const rowBg = row.is_invoice
                            ? T.successBg
                            : isEven
                              ? T.baseRow
                              : T.altRow;
                          const stickyBg = {
                            bgcolor: `${rowBg} !important`,
                            zIndex: 1,
                          };

                          return (
                            <TableRow
                              key={row.freight_id}
                              hover
                              sx={{
                                "& > td": {
                                  bgcolor: rowBg,
                                  transition: "background-color 0.12s",
                                },
                                "&:hover > td": {
                                  bgcolor: `${T.rowHover} !important`,
                                },
                              }}
                            >
                              {/* Checkbox - sticky */}
                              <TableCell
                                padding="checkbox"
                                sx={{
                                  position: "sticky",
                                  left: 0,
                                  ...stickyBg,
                                  p: "2px 4px",
                                }}
                              >
                                <Checkbox
                                  checked={row.is_invoice || false}
                                  onChange={() =>
                                    handleIsBillToggle(row.freight_id)
                                  }
                                  size="small"
                                  sx={{
                                    p: 0,
                                    color: alpha(brand, 0.4),
                                    "&.Mui-checked": { color: T.success },
                                  }}
                                />
                              </TableCell>

                              {/* S.No - sticky */}
                              <TableCell
                                sx={{
                                  position: "sticky",
                                  left: 36,
                                  ...stickyBg,
                                  fontWeight: 700,
                                  color: T.textMuted,
                                }}
                              >
                                {index + 1}
                              </TableCell>

                              {/* LR Number */}
                              <TableCell>
                                <Chip
                                  label={row.lr_number || `#${row.freight_id}`}
                                  size="small"
                                  sx={{
                                    ...sq,
                                    height: 16,
                                    bgcolor: T.primaryBg,
                                    color: T.primary,
                                    border: `1px solid ${T.border}`,
                                    fontSize: "0.6rem",
                                    fontWeight: 700,
                                    "& .MuiChip-label": { px: 0.5 },
                                  }}
                                />
                              </TableCell>

                              {/* Party Name */}
                              <TableCell>
                                <Tooltip title={row.party_name || "—"} arrow>
                                  <Typography
                                    sx={{
                                      fontSize: "0.72rem",
                                      fontWeight: 500,
                                      maxWidth: 120,
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    {row.party_name || "—"}
                                  </Typography>
                                </Tooltip>
                              </TableCell>

                              {/* Truck No */}
                              <TableCell>
                                <Chip
                                  label={row.truck_no || "—"}
                                  size="small"
                                  sx={{
                                    ...sq,
                                    height: 16,
                                    bgcolor: alpha("#ff9800", 0.08),
                                    color: "#e65100",
                                    border: `1px solid ${alpha("#ff9800", 0.25)}`,
                                    fontSize: "0.6rem",
                                    fontWeight: 700,
                                    "& .MuiChip-label": { px: 0.5 },
                                  }}
                                />
                              </TableCell>

                              <TableCell sx={{ textAlign: "left" }}>
                                {row.from_location || "—"}
                              </TableCell>
                              <TableCell sx={{ textAlign: "left" }}>
                                {row.to_location || "—"}
                              </TableCell>
                              <TableCell>{row.loading_wt ?? "—"}</TableCell>
                              <TableCell>{row.received_wt ?? "—"}</TableCell>

                              {/* Net Payable */}
                              <TableCell>
                                <Typography
                                  sx={{
                                    fontWeight: 700,
                                    fontSize: "0.72rem",
                                    color: T.primary,
                                  }}
                                >
                                  ₹{row.net_payable ?? 0}
                                </Typography>
                              </TableCell>

                              <TableCell>
                                {formatDate(row.loading_date)}
                              </TableCell>

                              <TableCell sx={{ textAlign: "left" }}>
                                {row.truck_owner || "—"}
                              </TableCell>

                              {/* Remark */}
                              <TableCell>
                                <Tooltip title={row.remark || "—"} arrow>
                                  <Typography
                                    sx={{
                                      fontSize: "0.68rem",
                                      maxWidth: 100,
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                      color: T.textMuted,
                                    }}
                                  >
                                    {row.remark || "—"}
                                  </Typography>
                                </Tooltip>
                              </TableCell>
                            </TableRow>
                          );
                        })
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </InfiniteScroll>
            </Box>
          </Paper>
        </Box>
      </Box>

      {/* ─── Footer Actions ──────────────────────────────────── */}
      <Box
        sx={{
          flexShrink: 0,
          display: "flex",
          justifyContent: "flex-end",
          gap: 1,
          px: 2,
          py: 1.25,
          bgcolor: T.white,
          borderTop: `1px solid ${T.border}`,
        }}
      >
        <Button
          size="small"
          onClick={onClose}
          sx={{ ...ghostBtn, height: 32, px: 2 }}
        >
          Cancel
        </Button>
        <Button
          size="small"
          startIcon={<SaveIcon sx={{ fontSize: 14 }} />}
          onClick={handleSubmit}
          sx={{ ...primaryBtn, height: 32, px: 2.5 }}
        >
          Save Payment
        </Button>
      </Box>
    </Box>
  );
};

export default EditFreightPayment;
